import { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/store/auth.store";
import { getCachedThumbnail, setCachedThumbnail } from "./ThumbnailCache";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

/**
 * ErrorFallback - Default error state display
 */
const ErrorFallback = ({ t }) => (
  <div className="flex flex-col items-center justify-center text-muted-foreground">
    <AlertCircle className="h-8 w-8 mb-2" aria-hidden="true" />
    <span className="text-xs">{t("common.previewUnavailable")}</span>
  </div>
);

/**
 * PdfThumbnail - Renders a thumbnail preview of the first page of a PDF
 *
 * Features:
 * - In-memory caching to prevent re-rendering
 * - Automatic cache invalidation based on URL
 * - Lazy loading with IntersectionObserver
 * - Graceful error handling with fallback
 *
 * @param {string} url - URL to fetch the PDF from (can be a blob URL or API endpoint)
 * @param {Blob} blob - Direct blob data (alternative to url)
 * @param {string} cacheKey - Custom cache key (defaults to url)
 * @param {string} className - Additional CSS classes for the container
 * @param {number} width - Target width for the thumbnail (default: 200)
 * @param {number} pageNumber - Page number to render (default: 1)
 * @param {Function} onClick - Click handler
 * @param {React.ReactNode} overlay - Content to overlay on the thumbnail
 * @param {React.ReactNode} fallback - Custom fallback content when PDF can't be loaded
 * @param {boolean} lazy - Enable lazy loading with IntersectionObserver (default: false)
 */
export const PdfThumbnail = ({
  url,
  blob,
  cacheKey,
  className,
  width = 200,
  pageNumber = 1,
  onClick,
  overlay,
  fallback,
  lazy = false,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const observerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [cachedImage, setCachedImage] = useState(null);
  const [isVisible, setIsVisible] = useState(!lazy);

  // Get auth token from store
  const accessToken = useAuthStore((state) => state.accessToken);

  // Generate cache key
  const effectiveCacheKey =
    cacheKey || url || (blob ? `blob-${blob.size}` : null);

  // Lazy loading with IntersectionObserver
  useEffect(() => {
    if (!lazy) {
      setIsVisible(true);
      return;
    }

    const element = containerRef.current;
    if (!element) {
      setIsVisible(true);
      return;
    }

    const checkViewport = () => {
      const rect = element.getBoundingClientRect();
      return (
        rect.top < window.innerHeight + 100 &&
        rect.bottom > -100 &&
        rect.left < window.innerWidth + 100 &&
        rect.right > -100
      );
    };

    if (checkViewport()) {
      setIsVisible(true);
      return;
    }

    const rafId = requestAnimationFrame(() => {
      if (checkViewport()) {
        setIsVisible(true);
        return;
      }

      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsVisible(true);
              observerRef.current?.disconnect();
            }
          });
        },
        {
          rootMargin: "100px",
          threshold: 0,
        },
      );

      observerRef.current.observe(element);
    });

    return () => {
      cancelAnimationFrame(rafId);
      observerRef.current?.disconnect();
    };
  }, [lazy]);

  // Reset state when source changes
  useEffect(() => {
    setCachedImage(null);
    setLoading(true);
    setError(false);
  }, [effectiveCacheKey, url, blob]);

  // Check cache first
  useEffect(() => {
    if (!isVisible) return;

    const cached = getCachedThumbnail(effectiveCacheKey);
    if (cached) {
      setCachedImage(cached.dataUrl);
      setDimensions({ width: cached.width, height: cached.height });
      setLoading(false);
      setError(false);
    }
  }, [effectiveCacheKey, isVisible]);

  // Render thumbnail
  useEffect(() => {
    if (!isVisible) return;
    if (cachedImage) return;

    const apiBaseUrl = import.meta.env.VITE_API_URL || "";
    const isApiUrl =
      url && (url.startsWith("/api/") || url.startsWith(apiBaseUrl));
    if (isApiUrl && !accessToken) {
      return;
    }

    let cancelled = false;
    let pdfDoc = null;

    const renderThumbnail = async () => {
      try {
        setLoading(true);
        setError(false);

        let pdfData;

        if (blob) {
          pdfData = await blob.arrayBuffer();
        } else if (url) {
          const headers = {};

          if (isApiUrl && accessToken) {
            headers["Authorization"] = `Bearer ${accessToken}`;
          }

          const response = await fetch(url, {
            credentials: "include",
            headers,
          });

          if (!response.ok) {
            throw new Error("Failed to fetch PDF");
          }

          pdfData = await response.arrayBuffer();
        } else {
          throw new Error("No PDF source provided");
        }

        if (cancelled) return;

        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        pdfDoc = await loadingTask.promise;

        if (cancelled) return;

        const page = await pdfDoc.getPage(pageNumber);

        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        const scale = width / viewport.width;
        const scaledViewport = page.getViewport({ scale });

        const offscreenCanvas = document.createElement("canvas");
        const context = offscreenCanvas.getContext("2d");
        offscreenCanvas.width = scaledViewport.width;
        offscreenCanvas.height = scaledViewport.height;

        await page.render({
          canvasContext: context,
          viewport: scaledViewport,
        }).promise;

        if (cancelled) return;

        const dataUrl = offscreenCanvas.toDataURL("image/png");

        setCachedThumbnail(
          effectiveCacheKey,
          dataUrl,
          scaledViewport.width,
          scaledViewport.height,
        );

        setCachedImage(dataUrl);
        setDimensions({
          width: scaledViewport.width,
          height: scaledViewport.height,
        });
        setLoading(false);
      } catch (err) {
        console.error("Error rendering PDF thumbnail:", err);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    };

    if (url || blob) {
      renderThumbnail();
    } else {
      setLoading(false);
      setError(true);
    }

    return () => {
      cancelled = true;
      if (pdfDoc) {
        pdfDoc.destroy();
      }
    };
  }, [
    url,
    blob,
    width,
    pageNumber,
    accessToken,
    effectiveCacheKey,
    isVisible,
    cachedImage,
  ]);

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative bg-muted flex items-center justify-center overflow-hidden",
        onClick && "cursor-pointer",
        className,
      )}
      onClick={onClick}
    >
      {loading && !cachedImage && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2
            className="h-6 w-6 animate-spin text-muted-foreground"
            aria-hidden="true"
          />
        </div>
      )}

      {error && !loading && (fallback || <ErrorFallback t={t} />)}

      {cachedImage && !error && (
        <img
          src={cachedImage}
          alt="PDF thumbnail"
          className="max-w-full max-h-full object-contain"
          style={{
            width: dimensions.width ? `${dimensions.width}px` : "auto",
            height: dimensions.height ? `${dimensions.height}px` : "auto",
          }}
        />
      )}

      <canvas ref={canvasRef} className="hidden" aria-hidden="true" />

      {overlay && cachedImage && !error && (
        <div className="absolute inset-0">{overlay}</div>
      )}
    </div>
  );
};

/**
 * PdfThumbnailFromApi - Convenience component that fetches PDF from an API endpoint
 * Handles authentication automatically
 */
export const PdfThumbnailFromApi = ({
  endpoint,
  token,
  cacheKey,
  className,
  width = 200,
  pageNumber = 1,
  onClick,
  overlay,
  fallback,
  lazy = false,
}) => {
  const [blobUrl, setBlobUrl] = useState(null);

  const accessToken = useAuthStore((state) => state.accessToken);
  const authToken = token || accessToken;

  useEffect(() => {
    if (!authToken && endpoint?.startsWith("/api/")) {
      return;
    }

    let cancelled = false;
    let objectUrl = null;

    const fetchPdf = async () => {
      try {
        const headers = {};
        if (authToken) {
          headers["Authorization"] = `Bearer ${authToken}`;
        }

        const response = await fetch(endpoint, {
          credentials: "include",
          headers,
        });

        if (!response.ok) {
          throw new Error("Failed to fetch PDF");
        }

        const blob = await response.blob();

        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch (err) {
        console.error("Error fetching PDF:", err);
      }
    };

    if (endpoint) {
      fetchPdf();
    }

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [endpoint, authToken]);

  return (
    <PdfThumbnail
      url={blobUrl}
      cacheKey={cacheKey || endpoint}
      className={className}
      width={width}
      pageNumber={pageNumber}
      onClick={onClick}
      overlay={overlay}
      fallback={fallback}
      lazy={lazy}
    />
  );
};

/**
 * PdfPageThumbnails - Renders thumbnails for multiple pages of a PDF
 * Useful for page navigation sidebars
 */
export const PdfPageThumbnails = ({
  url,
  blob,
  pageCount,
  selectedPage,
  onPageSelect,
  width = 120,
  className,
}) => {
  if (!pageCount || pageCount < 1) return null;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {Array.from({ length: pageCount }, (_, i) => i + 1).map((pageNum) => (
        <button
          key={pageNum}
          onClick={() => onPageSelect?.(pageNum)}
          aria-label={`Go to page ${pageNum}`}
          aria-pressed={selectedPage === pageNum}
          className={cn(
            "relative rounded-lg overflow-hidden border-2 transition-all",
            selectedPage === pageNum
              ? "border-primary ring-2 ring-primary/20"
              : "border-transparent hover:border-muted-foreground/30",
          )}
        >
          <PdfThumbnail
            url={url}
            blob={blob}
            cacheKey={`${url || "blob"}-page-${pageNum}`}
            width={width}
            pageNumber={pageNum}
            lazy
          />
          <div className="absolute bottom-1 right-1 bg-background/80 px-1.5 py-0.5 rounded text-xs font-medium">
            {pageNum}
          </div>
        </button>
      ))}
    </div>
  );
};

export default PdfThumbnail;
